/**
 * Composite Analyzer: ATM Tuning Suggestion (Phase 17)
 *
 * Forward-looking. Synthesizes per-template version trends + cross-template
 * directional patterns into CONCRETE "consider X next" suggestions for
 * templates that are RIPE for a tuning step.
 *
 * Two signal paths feed this analyzer:
 *
 *   PATH A — single-template extrapolation:
 *     A template has shown monotonic improvement across 3+ versions on a
 *     specific numeric param (e.g., Trail Freq 12→16→24). The next step
 *     value is extrapolated from the average step delta. Confidence is
 *     high because the trader's own data is the evidence.
 *
 *   PATH B — cross-template transfer:
 *     The trader has a strong cross-template direction (e.g., widening
 *     Trail SL consistently improves WR — 4/4 templates). Some other
 *     template has NOT had that change applied yet. Suggest applying
 *     the proven direction to the untouched template, using a step size
 *     borrowed from the trader's typical adjustments on similar templates.
 *
 * Why these are separate observations:
 *   The two recommendation types come from different evidence shapes and
 *   need different framing. PATH A is "the trend extrapolates to X";
 *   PATH B is "the pattern hasn't been tried on these templates yet."
 *   Mashing them together would dilute the message.
 *
 * Reads:
 *   ctx.allTrades (enriched with netPnl)
 *   ctx.ntTemplateVersions
 *
 * ─── Voice rebalance (2026-05-27) ───────────────────────────────────────
 *
 * This analyzer was already mostly forward-looking ("try X next") rather
 * than verdict-y, so the rebalance changes here are smaller than in
 * atmVersionComparison or atmParamDirection. Still: imperatives like
 * "Push X from N to M" and "Pick one — apply the same adjustment" got
 * softened to options the trader can take, and the PATH B title moved
 * from "Apply your proven pattern" (command) to "Your pattern hasn't
 * been tried on these templates yet" (invitation).
 *
 * BEFORE (PATH A action)
 *   "Push B1 SL from 24 to 30 on this template's next iteration. Hold
 *    other params constant so you can isolate the effect."
 *
 * AFTER (PATH A action)
 *   "If you're tuning this one next, 30 is the natural extrapolation —
 *    24 → 30 follows the trend you've already built. Holding other
 *    params constant would isolate the effect cleanly if you want a
 *    clean read."
 * ─────────────────────────────────────────────────────────────────────────
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import {
  findVersionForTrade,
  diffAtmVersions,
  diffFieldLabel,
} from '../../../ntTemplateParser.js';

export const key = 'atmTuningSuggestion';
export const tier = 'free';

// Path A — single-template extrapolation thresholds
const MIN_VERSIONS_FOR_EXTRAPOLATION = 3;
const MIN_TRADES_PER_VERSION = 3;
const MIN_WR_GAIN_PER_STEP = 0.05;       // each iteration must add ≥5pp WR

// Path B — cross-template transfer thresholds
const MIN_TEMPLATES_AGREE = 3;            // need ≥3 templates showing the same proven direction
const MIN_AGREEMENT_PCT = 0.75;
const MIN_WR_DELTA = 0.10;                // delta that "counts" as a winning change

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const computeWR = (trades) => {
  if (!Array.isArray(trades) || trades.length === 0) return 0;
  const wins = trades.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
  return wins / trades.length;
};

/**
 * Extract per-bracket numeric param values from a version snapshot. Returns
 * a flat map: `${field}|${bracket}|${step}` → value, plus a list of the
 * fields present so Path B can find templates that lack a given param.
 */
const flattenParams = (snapshot) => {
  const out = new Map();
  if (!snapshot?.brackets) return out;
  for (let i = 0; i < snapshot.brackets.length; i++) {
    const b = snapshot.brackets[i];
    if (typeof b.stopLossTicks === 'number') out.set(`stopLossTicks|${i}|`, b.stopLossTicks);
    if (typeof b.targetTicks === 'number') out.set(`targetTicks|${i}|`, b.targetTicks);
    if (typeof b.quantity === 'number') out.set(`quantity|${i}|`, b.quantity);
    if (b.stopStrategy) {
      const ss = b.stopStrategy;
      if (typeof ss.autoBreakEvenPlus === 'number') out.set(`autoBreakEvenPlus|${i}|`, ss.autoBreakEvenPlus);
      if (typeof ss.autoBreakEvenProfitTrigger === 'number') out.set(`autoBreakEvenProfitTrigger|${i}|`, ss.autoBreakEvenProfitTrigger);
      const steps = ss.autoTrailSteps || [];
      for (let s = 0; s < steps.length; s++) {
        if (typeof steps[s].frequency === 'number') out.set(`trail_frequency|${i}|${s}`, steps[s].frequency);
        if (typeof steps[s].profitTrigger === 'number') out.set(`trail_profitTrigger|${i}|${s}`, steps[s].profitTrigger);
        if (typeof steps[s].stopLoss === 'number') out.set(`trail_stopLoss|${i}|${s}`, steps[s].stopLoss);
      }
    }
  }
  return out;
};

/**
 * For a single ATM template, find numeric params that have improved
 * monotonically over 3+ versions. Returns the extrapolation suggestions.
 */
const findExtrapolationCandidates = (atmName, versions, tradesForThisAtm) => {
  if (!Array.isArray(versions) || versions.length < MIN_VERSIONS_FOR_EXTRAPOLATION) return [];

  const byVersion = new Map();
  for (const t of tradesForThisAtm) {
    const v = findVersionForTrade(t, versions);
    if (!v?.versionId) continue;
    if (!byVersion.has(v.versionId)) byVersion.set(v.versionId, []);
    byVersion.get(v.versionId).push(t);
  }

  const chrono = [...versions].reverse();
  // Filter to versions that have enough trades
  const usable = chrono.filter(v => (byVersion.get(v.versionId) || []).length >= MIN_TRADES_PER_VERSION);
  if (usable.length < MIN_VERSIONS_FOR_EXTRAPOLATION) return [];

  // Compute WR for each usable version
  const wrByVid = new Map(usable.map(v => [v.versionId, computeWR(byVersion.get(v.versionId))]));

  // Check WR is monotonically improving overall — at least the first→last
  // must show gain, and each step must add ≥ MIN_WR_GAIN_PER_STEP
  const wrSeries = usable.map(v => wrByVid.get(v.versionId));
  for (let i = 1; i < wrSeries.length; i++) {
    if (wrSeries[i] - wrSeries[i - 1] < MIN_WR_GAIN_PER_STEP) return [];
  }

  // For each parameter that exists in all usable versions, check
  // monotonic direction. Extrapolate the next step.
  const allParams = new Set();
  const flatPerVersion = usable.map(v => flattenParams(v.snapshot));
  for (const m of flatPerVersion) for (const k of m.keys()) allParams.add(k);

  const suggestions = [];
  for (const paramKey of allParams) {
    const sequence = flatPerVersion.map(m => m.get(paramKey));
    if (sequence.some(v => typeof v !== 'number')) continue;  // param not present in every version
    let allUp = true, allDown = true;
    for (let i = 1; i < sequence.length; i++) {
      const delta = sequence[i] - sequence[i - 1];
      if (delta <= 0) allUp = false;
      if (delta >= 0) allDown = false;
    }
    if (!allUp && !allDown) continue;  // not monotonic

    // Avg step size
    let totalStep = 0;
    for (let i = 1; i < sequence.length; i++) totalStep += sequence[i] - sequence[i - 1];
    const avgStep = totalStep / (sequence.length - 1);
    const last = sequence[sequence.length - 1];
    const nextValue = Math.round(last + avgStep);

    const [field, bracket, step] = paramKey.split('|');
    suggestions.push({
      atmName,
      field,
      bracket: bracket === '' ? null : parseInt(bracket, 10),
      step: step === '' ? null : parseInt(step, 10),
      sequence,
      avgStep,
      nextValue,
      direction: allUp ? 'increased' : 'decreased',
      wrSequence: wrSeries,
    });
  }

  return suggestions;
};

/**
 * Compute the cross-template "proven directions" map for Path B.
 * Returns map: `${field}|${direction}` → { templatesCount, avgWrDelta,
 * templatesAlreadyChanged: Set, sampleStepSize }.
 */
const computeProvenDirections = (allTrades, ntTemplateVersions) => {
  const buckets = new Map();
  const templatesAlreadyChanged = new Map();  // `${field}|${direction}` → Set of atmNames

  for (const atmName of Object.keys(ntTemplateVersions)) {
    const versions = ntTemplateVersions[atmName];
    if (!Array.isArray(versions) || versions.length < 2) continue;
    const tradesForThisAtm = allTrades.filter(t => t.atmStrategy === atmName);
    if (tradesForThisAtm.length === 0) continue;

    const byVersion = new Map();
    for (const t of tradesForThisAtm) {
      const v = findVersionForTrade(t, versions);
      if (!v?.versionId) continue;
      if (!byVersion.has(v.versionId)) byVersion.set(v.versionId, []);
      byVersion.get(v.versionId).push(t);
    }

    const chrono = [...versions].reverse();
    for (let i = 1; i < chrono.length; i++) {
      const prior = chrono[i - 1];
      const current = chrono[i];
      const priorTrades = byVersion.get(prior.versionId) || [];
      const currentTrades = byVersion.get(current.versionId) || [];
      if (priorTrades.length < MIN_TRADES_PER_VERSION) continue;
      if (currentTrades.length < MIN_TRADES_PER_VERSION) continue;
      const wrDelta = computeWR(currentTrades) - computeWR(priorTrades);

      const changes = diffAtmVersions(prior, current);
      for (const c of changes) {
        if (typeof c.old !== 'number' || typeof c.new !== 'number') continue;
        const direction = c.new > c.old ? 'increased' : (c.new < c.old ? 'decreased' : null);
        if (!direction) continue;
        const bucketKey = `${c.field}|${direction}`;
        if (!buckets.has(bucketKey)) buckets.set(bucketKey, { records: [] });
        buckets.get(bucketKey).records.push({ atmName, oldVal: c.old, newVal: c.new, wrDelta, stepSize: Math.abs(c.new - c.old) });
        if (!templatesAlreadyChanged.has(bucketKey)) templatesAlreadyChanged.set(bucketKey, new Set());
        templatesAlreadyChanged.get(bucketKey).add(atmName);
      }
    }
  }

  // Filter to "proven directions" — buckets where ≥ MIN_TEMPLATES_AGREE
  // templates show the direction AND ≥ MIN_AGREEMENT_PCT improved WR
  const proven = new Map();
  for (const [bucketKey, b] of buckets) {
    if (b.records.length < MIN_TEMPLATES_AGREE) continue;
    const improved = b.records.filter(r => r.wrDelta >= MIN_WR_DELTA).length;
    const improvedShare = improved / b.records.length;
    if (improvedShare < MIN_AGREEMENT_PCT) continue;
    // Median step size — robust to outliers
    const sorted = [...b.records].map(r => r.stepSize).sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    proven.set(bucketKey, {
      records: b.records,
      templatesAlreadyChanged: templatesAlreadyChanged.get(bucketKey),
      avgWrDelta: b.records.reduce((s, r) => s + r.wrDelta, 0) / b.records.length,
      typicalStepSize: median,
    });
  }
  return proven;
};

/**
 * Task A.2 — fields that only have meaning on AT-style brackets (trail
 * mechanics). On a pure TP template the trail_* fields don't exist on the
 * bracket structure, so flattenParams wouldn't surface them anyway — but
 * we make the rule explicit so Path B doesn't suggest a "widen trail_stopLoss"
 * change on a TP template that has no trail logic at all. Hybrid templates
 * (mix of AT and TP brackets) are fine targets — they have at least one AT
 * bracket where the trail field lives.
 *
 * Note: this is the FIELD's compatibility list, not the field's typeBias.
 * stopLossTicks/targetTicks/quantity exist on any bracket regardless of type
 * so they don't appear here.
 */
const AT_ONLY_FIELDS = new Set([
  'trail_frequency',
  'trail_profitTrigger',
  'trail_stopLoss',
  'autoBreakEvenPlus',
  'autoBreakEvenProfitTrigger',
]);

const isFieldCompatibleWithType = (field, templateType) => {
  if (!AT_ONLY_FIELDS.has(field)) return true;       // SL/TP/quantity work anywhere
  // For AT-only fields, only BLOCK when we KNOW the target is TP.
  // null/undefined/'Unknown' → permissive (could be AT, could be a template
  // whose XML didn't parse cleanly). Falls back to legacy behavior in those
  // cases — flattenParams won't surface a trail_* field on a template that
  // genuinely lacks it anyway, so the explicit gate is belt + suspenders.
  if (templateType === 'TP') return false;
  return true;
};

/**
 * For each proven direction, find templates that haven't had the change
 * applied yet — those are the "transfer candidates."
 */
const findTransferCandidates = (provenDirections, ntTemplateVersions) => {
  const candidates = [];
  for (const [bucketKey, info] of provenDirections) {
    const [field, direction] = bucketKey.split('|');
    for (const atmName of Object.keys(ntTemplateVersions)) {
      if (info.templatesAlreadyChanged.has(atmName)) continue;  // already iterated this direction
      const versions = ntTemplateVersions[atmName];
      const latest = versions?.[0];  // newest-first
      if (!latest?.snapshot) continue;
      // Task A.2 — type-aware transfer safety. Skip targets where the field
      // is meaningless under the current management style. Prevents Path B
      // from recommending "widen trail_stopLoss" on a take-profit-only
      // template that has nothing to trail.
      const targetTemplateType = latest.snapshot.templateType || null;
      if (!isFieldCompatibleWithType(field, targetTemplateType)) continue;
      const params = flattenParams(latest.snapshot);
      // Find any matching param in this template
      const paramEntries = [...params.entries()].filter(([k]) => k.startsWith(`${field}|`));
      if (paramEntries.length === 0) continue;
      for (const [paramKey, currentValue] of paramEntries) {
        const [, bracket, step] = paramKey.split('|');
        const stepDelta = direction === 'increased' ? info.typicalStepSize : -info.typicalStepSize;
        candidates.push({
          atmName,
          field,
          bracket: bracket === '' ? null : parseInt(bracket, 10),
          step: step === '' ? null : parseInt(step, 10),
          currentValue,
          suggestedValue: Math.max(1, Math.round(currentValue + stepDelta)),
          direction,
          basedOnTemplates: info.records.length,
          avgWrDelta: info.avgWrDelta,
          targetTemplateType,  // Task A.2 — exposed for evidence/debug
        });
      }
    }
  }
  return candidates;
};

export const analyze = (ctx) => {
  const { allTrades = [], ntTemplateVersions = {} } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;
  if (!ntTemplateVersions || Object.keys(ntTemplateVersions).length === 0) return null;

  // Live-trade gate: extrapolation + cross-template transfer both depend on
  // realized per-version WR (computeWR(trades)). Open trades have unrealized
  // P&L which isn't a realized outcome — including them would fire premature
  // "try X next" suggestions based on incomplete data. Matches summarizePnl
  // semantics. Filtered once at the top so all downstream per-template
  // subsets inherit the closed-only constraint.
  const closedAllTrades = allTrades.filter(t => t?.isOpen !== true);
  if (closedAllTrades.length === 0) return null;

  const observations = [];

  // ── PATH A — single-template extrapolation ───────────────────────
  for (const atmName of Object.keys(ntTemplateVersions)) {
    const versions = ntTemplateVersions[atmName];
    const tradesForThisAtm = closedAllTrades.filter(t => t.atmStrategy === atmName);
    if (tradesForThisAtm.length === 0) continue;
    const suggestions = findExtrapolationCandidates(atmName, versions, tradesForThisAtm);
    if (suggestions.length === 0) continue;

    // Pick the highest-WR-gain suggestion to surface (one per template
    // to avoid spamming the feed with bracket micro-tweaks)
    const top = suggestions[0];
    const fieldLabel = diffFieldLabel(top.field);
    const where = [top.bracket != null ? `B${top.bracket + 1}` : '', top.step != null ? `s${top.step + 1}` : ''].filter(Boolean).join(' ');
    const wrStart = top.wrSequence[0];
    const wrEnd = top.wrSequence[top.wrSequence.length - 1];

    // Task A.2 — surface the template's current style so downstream
    // consumers can group/filter suggestions by management style.
    const atmTemplateType = versions?.[0]?.snapshot?.templateType || null;

    observations.push(makeObservation({
      id: `${key}:${atmName}:extrapolate:${top.field}-${top.bracket ?? ''}-${top.step ?? ''}`,
      analyzer: key,
      scope: 'atm',
      scopeId: atmName,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.SETUP,
      title: `"${atmName}" — ${where ? where + ' ' : ''}${fieldLabel} ${top.nextValue} would be the natural next step`,
      detail: `Sequence ${top.sequence.join('→')} has improved WR from ${fmtPct(wrStart)} to ${fmtPct(wrEnd)} (${top.sequence.length} versions). Avg step is ${Math.abs(Math.round(top.avgStep))}; extrapolating forward suggests ${top.nextValue}.`,
      action: `If you're tuning this one next, ${top.nextValue} is the natural extrapolation — ${top.sequence[top.sequence.length - 1]} → ${top.nextValue} follows the trend you've already built. Holding other params constant would isolate the effect cleanly if you want a clean read on whether the trend keeps going or starts to plateau.`,
      evidence: {
        path: 'extrapolation',
        atmName,
        field: top.field,
        bracket: top.bracket,
        step: top.step,
        sequence: top.sequence,
        suggestedNext: top.nextValue,
        avgStep: top.avgStep,
        wrSequence: top.wrSequence,
        templateType: atmTemplateType,  // Task A.2
      },
      confidence: 0.7,
      priority: 76,
    }));
  }

  // ── PATH B — cross-template transfer ──────────────────────────────
  const proven = computeProvenDirections(closedAllTrades, ntTemplateVersions);
  const transferCandidates = findTransferCandidates(proven, ntTemplateVersions);

  // Group transfer candidates by (field, direction) so we surface ONE
  // observation per direction with a top-3 candidate list — not dozens
  // of identical-pattern observations.
  const byDirection = new Map();
  for (const c of transferCandidates) {
    const k = `${c.field}|${c.direction}`;
    if (!byDirection.has(k)) byDirection.set(k, []);
    byDirection.get(k).push(c);
  }

  for (const [dirKey, candidates] of byDirection) {
    if (candidates.length === 0) continue;
    const [field, direction] = dirKey.split('|');
    const fieldLabel = diffFieldLabel(field);
    // Trader-natural direction verb. Frequency is inverted vs distance
    // params: HIGHER frequency = looser trail (less responsive), LOWER =
    // tighter. Match atmParamDirection.js so the two analyzers don't
    // disagree about what to call the same change. See that file for the
    // full context on the v7.6.0 LLM-synthesis bug this fix prevents.
    const directionWord = field.includes('frequency')
      ? (direction === 'increased' ? 'looser' : 'tighter')
      : (direction === 'increased' ? 'wider' : 'tighter');
    // Top 3 candidates — keep the detail line readable
    const topCandidates = candidates.slice(0, 3);
    const candidateText = topCandidates.map(c => {
      const where = [c.bracket != null ? `B${c.bracket + 1}` : '', c.step != null ? `s${c.step + 1}` : ''].filter(Boolean).join(' ');
      return `${c.atmName.slice(0, 28)} (${where ? where + ' ' : ''}${c.currentValue}→${c.suggestedValue})`;
    }).join('; ');

    observations.push(makeObservation({
      id: `${key}:transfer:${field}-${direction}`,
      analyzer: key,
      scope: 'global',
      scopeId: `transfer:${field}:${direction}`,
      severity: SEVERITY.INFO,
      category: CATEGORY.SETUP,
      title: `Your "${directionWord} ${fieldLabel}" pattern hasn't been tried on ${candidates.length} other template${candidates.length === 1 ? '' : 's'} yet`,
      detail: `${topCandidates[0].basedOnTemplates} templates have shown ${directionWord} ${fieldLabel} improves WR by avg ${fmtPct(topCandidates[0].avgWrDelta)}. These templates haven't been adjusted in that direction yet: ${candidateText}.`,
      action: `Worth picking one and applying the same ${directionWord} adjustment — the WR delta on that single template would tell you whether the cross-template pattern actually generalizes or whether something about the other templates' setups was carrying it. Either answer is useful.`,
      evidence: {
        path: 'transfer',
        field,
        direction,
        candidates: topCandidates,
        totalUntouched: candidates.length,
      },
      confidence: 0.65,
      priority: 70,
    }));
  }

  return observations.length > 0 ? observations : null;
};

// Test-friendly exports
export {
  flattenParams,
  findExtrapolationCandidates,
  computeProvenDirections,
  findTransferCandidates,
  isFieldCompatibleWithType,
  AT_ONLY_FIELDS,
  MIN_VERSIONS_FOR_EXTRAPOLATION,
  MIN_TRADES_PER_VERSION,
  MIN_WR_GAIN_PER_STEP,
  MIN_TEMPLATES_AGREE,
  MIN_AGREEMENT_PCT,
  MIN_WR_DELTA,
};
